<template>
  <div
    class="c-file-upload"
    :class="{ 'new-design': newDesign }"
  >
    <slot name="button">
      <BaseButton
        :color="color"
        :disabled="uploading"
        icon="attach_file"
        :label="label"
        :loading="uploading"
        :new-design="newDesign"
        size="sm"
        @click="selectFile"
      />
    </slot>

    <slot />

    <input
      ref="fileInput"
      :accept="accept"
      class="tw:invisible"
      :multiple="multiple"
      type="file"
      @change="uploadFiles"
    />

    <div v-if="errors.length">
      <span
        v-for="(error, index) in errors"
        :key="index"
        class="text-error"
      >
        {{ error }}
      </span>
    </div>

    <div
      class="attachments-container q-mt-xs relative"
      :class="{ 'q-gutter-sm': !newDesign }"
    >
      <div
        v-if="!hideNoAttachmentWarning && !attachments.length && !files.length"
        class="field-subtext absolute-center"
      >
        {{ $t("file_upload.no_attachments") }}
      </div>

      <slot
        name="attachments"
        v-bind="attachments"
      >
        <FileUploadAttachment
          v-for="(attachment, index) in attachments"
          :key="index"
          :attachment="attachment"
          :downloadable="downloadable"
          :new-design="newDesign"
          @remove="removeFile(attachment)"
        />
      </slot>

      <template v-if="!hideAttachmentList">
        <FileUploadAttachment
          v-for="file in files"
          :key="file.name"
          :attachment="file"
          :new-design="newDesign"
          @cancel="cancelUpload(file)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { BaseButton } from "shared/components/base";
import {
  blobRedirectUrl,
  directUploadUrl,
  ONE_MB_IN_BYTES,
} from "shared/constants";
import { ActiveStorageAttachment, ActiveStorageBlob } from "shared/resources";

import FileUploadAttachment from "./FileUploadAttachment.vue";
import Uploader from "./Uploader";

const TEN_MB = ONE_MB_IN_BYTES * 10;

export default {
  name: "FileUpload",
  components: {
    BaseButton,
    FileUploadAttachment,
  },
  props: {
    modelValue: {
      type: [Array, Object],
      default: () => ({}),
    },
    attachmentField: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      default: "secondary",
    },
    maxFileSize: {
      type: [Number, Object],
      default: TEN_MB,
    },
    multiple: {
      type: Boolean,
    },
    accept: {
      type: String,
      default: "",
    },
    newDesign: {
      type: Boolean,
    },
    hideNoAttachmentWarning: {
      type: Boolean,
    },
    hideAttachmentList: {
      type: Boolean,
    },
    downloadable: {
      type: Boolean,
    },
  },
  emits: ["delete", "update:modelValue", "uploading"],
  data() {
    return {
      errors: [],
      files: [],
      uploadedBytes: 0,
      totalSize: 0,
    };
  },
  computed: {
    attachments() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue.filter(
          (attachment) => !attachment.isMarkedForDestruction
        );
      }

      if (
        Object.keys(this.modelValue).length &&
        !this.modelValue.isMarkedForDestruction
      ) {
        return [this.modelValue];
      }

      return [];
    },
    uploading() {
      return (
        this.files
          .map((attachment) => attachment.progress < attachment.total)
          .filter((uploading) => uploading).length > 0
      );
    },
    acceptedTypes() {
      return this.accept.split(",");
    },
  },
  watch: {
    uploading(value) {
      this.$emit("uploading", value);
    },
  },
  methods: {
    prettyFileSize(size) {
      const index = Math.floor(Math.log(size) / Math.log(1024));

      return `${Number((size / 1024 ** index).toFixed(2))} ${
        ["B", "kB", "MB", "GB", "TB"][index]
      }`;
    },
    selectFile() {
      this.$refs.fileInput.click();
    },
    removeFile(file) {
      this.$emit("delete", file);

      const index = this.files.indexOf(file);
      this.files.splice(index, 1);
    },
    async uploadFiles(event) {
      const promises = [];
      this.errors = [];
      this.totalSize = 0;

      Array.from(event.target.files).forEach((file) => {
        if (!this.validType(file)) {
          this.errors.push(
            this.$t("file_upload.not_accepted_type", { fileName: file.name })
          );

          return;
        }

        const maxSizeForFile = this.validFileSize(file);

        if (file.size > maxSizeForFile) {
          this.errors.push(
            this.$t("file_upload.file_exceeds_limit_size", {
              fileName: file.name,
              maxSizeForFile: this.prettyFileSize(maxSizeForFile),
            })
          );

          return;
        }

        const fileWrapper = {
          file,
          name: file.name,
          progress: 0,
          total: file.size,
        };

        this.totalSize += file.size;

        fileWrapper.uploader = this.createFileUploader(fileWrapper);

        this.files.push(fileWrapper);
        promises.push(fileWrapper.uploader.upload());
      });

      if (promises.length) {
        const results = await Promise.all(promises);

        const blobs = results.map((blob) => {
          const activeStorageBlob = new ActiveStorageBlob({
            ...blob,
            relativeUrl: this.relativeUrl(blob),
            video: blob.content_type.includes("video/"),
            image: blob.content_type.includes("image/"),
          });

          const attachment = new ActiveStorageAttachment({
            name: this.attachmentField,
            blobId: blob.id,
            activeStorageBlob,
          });

          return attachment;
        });

        if (this.multiple) {
          this.$emit("update:modelValue", [...this.modelValue, ...blobs]);
        } else {
          this.$emit("update:modelValue", blobs[0]);
        }

        this.files = [];
        this.$refs.fileInput.value = "";
      }
    },
    onProgress(fileWrapper) {
      return (event) => {
        const { loaded: progress, total } = event;
        this.uploadedBytes += progress;
        Object.assign(fileWrapper, { progress, total });
      };
    },
    createFileUploader(fileWrapper) {
      return new Uploader(fileWrapper.file, directUploadUrl, {
        progress: this.onProgress(fileWrapper),
      });
    },
    cancelUpload(fileWrapper) {
      fileWrapper.uploader.abort();
      this.removeFile(fileWrapper);
    },
    relativeUrl(blob) {
      return `${blobRedirectUrl}/${blob.signed_id}/${blob.filename}`;
    },
    validFileSize(file) {
      if (this.maxFileSize.constructor === Object) {
        return (
          this.maxFileSize[file.type] ||
          this.maxFileSize[file.type.split("/")[0]] ||
          TEN_MB
        );
      }

      return this.maxFileSize;
    },
    validType(file) {
      return (
        !this.accept.length ||
        this.acceptedTypes.includes(file.type) ||
        this.acceptedTypes.includes(`.${file.name.split(".").slice(-1)[0]}`)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.c-file-upload {
  &.new-design {
    display: flex;
    flex-direction: column-reverse;

    .base-button {
      align-self: end;
      margin-top: 8px;
    }

    .attachments-container {
      border: 1px solid var(--s-input-default-border);
      border-radius: 4px;
      min-height: 100px;
    }
  }
}
</style>
